<script setup>
import { useModal } from 'vue-final-modal';

import routes from '@/constants/routes';

const { t } = useI18n();
const authStore = useAuthStore();

const logoutModal = useModal({
  component: resolveComponent('ModalsConfirm'),
  attrs: {
    title: t('modals.logout.title'),
    buttonText: t('modals.logout.button'),
  },
});

async function handleLogout(close) {
  logoutModal.patchOptions({
    attrs: {
      async onConfirm() {
        await authStore.fetchGuestToken();

        await logoutModal.close();

        close();
      },
    },
  });

  return logoutModal.open();
}
</script>

<template>
  <ModalBase
    v-slot="{ close }"
    class="modals-settings"
    :title="t('modals.settings.title')"
  >
    <div class="modals-settings__container">
      <div class="modals-settings__items">
        <div v-if="authStore.account?.email" class="modals-settings__row">
          <div class="modals-settings__text">
            <span class="modals-settings__font modals-settings__font--text">
              {{ authStore.account.email }}
            </span>
          </div>

          <button
            class="modals-settings__button modals-settings__button--item"
            @click="handleLogout(close)"
          >
            <span class="modals-settings__font modals-settings__font--button">
              {{ t('modals.settings.logout') }}
            </span>
          </button>
        </div>
      </div>

      <div class="modals-settings__links">
        <a
          class="modals-settings__link"
          target="_blank"
          :href="routes.TERMS"
          @click="close"
        >
          <span class="modals-settings__font modals-settings__font--link">
            {{ t('modals.settings.terms') }}
          </span>
        </a>

        <a
          class="modals-settings__link"
          :href="routes.PRIVACY"
          target="_blank"
          @click="close"
        >
          <span class="modals-settings__font modals-settings__font--link">
            {{ t('modals.settings.privacy') }}
          </span>
        </a>
      </div>

      <UiButton
        class="modals-settings__button modals-settings__button--help"
        :text="t('modals.settings.help')"
        :to="routes.HELP"
        theme="secondary"
        @click="close"
      />
    </div>
  </ModalBase>
</template>

<style scoped lang="scss">
.modals-settings {
  &__font {
    &--text,
    &--button,
    &--link {
      font-size: em(18);
      font-weight: 400;
      line-height: 1;
    }
  }

  &__container {
    padding: em(13) em($modal-padding) em(38);

    @include media-breakpoint-down(sm) {
      padding: em(13) em($gap-sm) em($gap-sm);
    }
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: em(25);

    &:not(:empty) {
      margin-bottom: em(47);
    }
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__text {
    max-width: em(200);
    min-height: em(20);
    overflow: hidden;
    color: $color-black;
    text-overflow: ellipsis;
  }

  &__button {
    &--item {
      color: $color-main-1;
    }

    &--help {
      width: em(309);
      height: em(51);

      @include media-breakpoint-down(sm) {
        width: 100%;
      }
    }
  }

  &__links {
    display: flex;
    flex-direction: column;
    gap: em(36);
    align-items: flex-start;
    margin-bottom: em(55);
  }

  &__link {
    color: $color-main-1;
  }
}
</style>
